import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, Upload, Loader2, FileText } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { statusBadge } from './editorial';

export default function AdminBando() {
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [analyses, setAnalyses] = useState([]);

  const generate = async () => {
    if (!genSubject) { toast.error('Seleziona una materia'); return; }
    setGenerating(true);
    setGenerated(null);
    try {
      const { data } = await api.post(`/admin/bando/analyses/${result.analysis_id}/generate`, { subject_id: genSubject });
      setGenerated(data);
      toast.success('Bozza generata in stato AI_GENERATED — richiede revisione');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nella generazione');
    } finally {
      setGenerating(false);
    }
  };

  const loadAnalyses = () => api.get('/admin/bando/analyses').then(({ data }) => setAnalyses(data.items)).catch(() => {});
  useEffect(() => {
    loadAnalyses();
    api.get('/admin/subjects').then(({ data }) => setSubjects(data.items)).catch(() => {});
  }, []);

  const analyze = async () => {
    if (!text.trim() && !file) { toast.error('Incolla il testo del bando o carica un PDF'); return; }
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      if (text.trim()) fd.append('request_text', text);
      if (file) fd.append('file', file);
      const { data } = await api.post('/admin/bando/analyze', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(data.analysis);
      toast.success('Analisi completata — in attesa di revisione');
      loadAnalyses();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore durante l\'analisi');
    } finally {
      setLoading(false);
    }
  };

  const [subjects, setSubjects] = useState([]);
  const [genSubject, setGenSubject] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(null);

  const Ex = ({ label, value }) => (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <p className="text-xs tracking-[0.1em] uppercase text-slate-500 font-semibold mb-1.5">{label}</p>
      {Array.isArray(value) ? (
        <ul className="text-sm text-slate-700 space-y-1 list-disc pl-4">{value.map((v, i) => <li key={i}>{typeof v === 'object' ? JSON.stringify(v) : v}</li>)}</ul>
      ) : (
        <p className="text-sm text-slate-700">{value ?? '—'}</p>
      )}
    </div>
  );

  const ex = result?.extracted;
  const sug = ex?.suggerimenti;

  return (
    <div data-testid="admin-bando">
      <h2 className="font-display font-semibold text-lg text-slate-900 mb-1">Analisi bando con AI</h2>
      <p className="text-sm text-slate-500 mb-6">
        Carica un bando: l'AI estrae i dati chiave e propone i moduli riutilizzabili. Nessun contenuto viene pubblicato automaticamente: ogni analisi resta in stato TO_REVIEW.
      </p>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-8">
        <Label>Incolla il testo del bando</Label>
        <Textarea rows={6} className="mt-1.5 mb-4" data-testid="bando-text" value={text}
          onChange={(e) => setText(e.target.value)} placeholder="MINISTERO... Concorso pubblico, per esami, a n. 300 posti di..." />
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-600 border border-dashed border-slate-300 rounded-xl px-4 py-3 cursor-pointer hover:border-brand transition-colors">
            <Upload size={15} strokeWidth={1.5} />
            {file ? file.name : 'oppure carica il PDF del bando'}
            <input type="file" accept=".pdf,.txt" className="hidden" data-testid="bando-file"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
          <Button onClick={analyze} disabled={loading} data-testid="bando-analyze"
            className="rounded-full bg-brand hover:bg-brand-hover text-white px-6">
            {loading ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Sparkles size={15} strokeWidth={1.5} className="mr-2" />}
            {loading ? 'Analisi in corso...' : 'Analizza con AI'}
          </Button>
        </div>
      </div>

      {result && (
        <div className="mb-10" data-testid="bando-result">
          <div className="flex items-center gap-3 mb-4">
            <h3 className="font-display font-semibold text-slate-900">Risultato dell'analisi</h3>
            {statusBadge(result.status)}
          </div>
          {!ex ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-slate-700">
              <p className="font-semibold mb-2">Risposta non strutturata, testo grezzo:</p>
              <pre className="whitespace-pre-wrap text-xs">{result.raw_response}</pre>
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <Ex label="Ente" value={ex.ente} />
                <Ex label="Nome concorso" value={ex.nome_concorso} />
                <Ex label="Numero posti" value={ex.numero_posti} />
                <Ex label="Scadenze" value={ex.scadenze} />
                <Ex label="Requisiti" value={ex.requisiti} />
                <Ex label="Tipologia prove" value={ex.tipologia_prove} />
                <Ex label="Materie" value={ex.materie} />
                <Ex label="Riferimenti normativi" value={ex.riferimenti_normativi} />
              </div>
              {sug && (
                <div className="grid sm:grid-cols-3 gap-4" data-testid="bando-suggestions">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
                    <p className="text-xs tracking-[0.1em] uppercase text-emerald-700 font-semibold mb-3">Moduli riutilizzabili</p>
                    <ul className="text-sm text-slate-700 space-y-2">
                      {(sug.moduli_riutilizzabili || []).map((m, i) => <li key={i}><span className="font-medium">{m.title || m.module_id}</span>{m.motivo ? ` — ${m.motivo}` : ''}</li>)}
                      {!(sug.moduli_riutilizzabili || []).length && <li className="text-slate-400">Nessuno</li>}
                    </ul>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                    <p className="text-xs tracking-[0.1em] uppercase text-amber-700 font-semibold mb-3">Da aggiornare</p>
                    <ul className="text-sm text-slate-700 space-y-2">
                      {(sug.moduli_da_aggiornare || []).map((m, i) => <li key={i}><span className="font-medium">{m.title || m.module_id}</span>{m.motivo ? ` — ${m.motivo}` : ''}</li>)}
                    </ul>
                  </div>
                  <div className="bg-rose-50 border border-rose-200 rounded-xl p-5">
                    <p className="text-xs tracking-[0.1em] uppercase text-rose-700 font-semibold mb-3">Contenuti mancanti</p>
                    <ul className="text-sm text-slate-700 space-y-2 list-disc pl-4">
                      {(sug.contenuti_mancanti || []).map((m, i) => <li key={i}>{m}</li>)}
                      {!(sug.contenuti_mancanti || []).length && <li className="text-slate-400 list-none">Nessuno</li>}
                    </ul>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {result && ex && (
        <div className="bg-brand-light border border-brand/20 rounded-2xl p-6 mb-10" data-testid="bando-generate">
          <h3 className="font-display font-semibold text-slate-900 mb-1">Genera bozza di modulo da questa analisi</h3>
          <p className="text-sm text-slate-600 mb-4">
            L'AI crea modulo, capitoli e sezioni in stato <strong>AI_GENERATED</strong>: non saranno mai pubblici finché non li revisioni e approvi in Contenuti.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Select value={genSubject} onValueChange={setGenSubject}>
              <SelectTrigger className="sm:w-72 bg-white" data-testid="generate-subject"><SelectValue placeholder="Materia del modulo" /></SelectTrigger>
              <SelectContent>
                {subjects.map((s) => <SelectItem key={s.subject_id} value={s.subject_id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={generate} disabled={generating} data-testid="generate-draft"
              className="rounded-full bg-brand hover:bg-brand-hover text-white">
              {generating ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Sparkles size={15} strokeWidth={1.5} className="mr-2" />}
              {generating ? 'Generazione in corso...' : 'Genera bozza modulo'}
            </Button>
          </div>
          {generated && (
            <p className="mt-4 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3" data-testid="generate-result">
              Bozza "<strong>{generated.title}</strong>" creata: {generated.chapters} capitoli, {generated.sections} sezioni.
              Revisionala in Contenuti → Moduli e passa lo stato a APPROVED/PUBLISHED quando pronta.
            </p>
          )}
        </div>
      )}

      <h3 className="font-display font-semibold text-slate-900 mb-4">Analisi precedenti</h3>
      <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
        {analyses.length === 0 && <p className="text-sm text-slate-500 p-6">Nessuna analisi ancora effettuata.</p>}
        {analyses.map((a) => (
          <div key={a.analysis_id} className="flex items-center justify-between gap-4 px-6 py-4" data-testid={`analysis-${a.analysis_id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <FileText size={16} strokeWidth={1.5} className="text-slate-400 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">
                  {a.extracted?.nome_concorso || a.source_filename || 'Analisi bando'}
                </p>
                <p className="text-xs text-slate-400">{new Date(a.created_at).toLocaleString('it-IT')}</p>
              </div>
            </div>
            {statusBadge(a.status)}
          </div>
        ))}
      </div>
    </div>
  );
}
